import { useState, useEffect, Fragment } from 'react';
import { Loader2, Clock, Mic, FileText, Globe, Stethoscope, Euro, ChevronDown, ChevronRight, Phone } from 'lucide-react';
import { api } from '@/lib/api';
import type { VerbruikGroep } from '@/lib/api-types';
import { haalAlleRegels, jaarEnMaand } from '@/lib/verbruik';

interface UsageLog {
  id: string;
  usageType: string;
  seconds: number;
  createdAt: string;
  model: string | null;
  sellingCost: number;
}

/** Eén maand zoals dit scherm hem nodig heeft: zonder inkoop, want /me geeft die niet. */
type Maand = Pick<VerbruikGroep, 'month' | 'entries' | 'seconds' | 'selling'>;

const USAGE_TYPE_LABELS: Record<string, { label: string; icon: typeof Mic }> = {
  speech_recording: { label: 'Spraakopname', icon: Mic },
  soep_transcription: { label: 'SOEP-transcriptie', icon: FileText },
  translation: { label: 'Vertaling', icon: Globe },
  icpc_suggestion: { label: 'ICPC-suggestie', icon: Stethoscope },
  patient_summary: { label: 'Patiëntsamenvatting (PDF)', icon: FileText },
  acs_call: { label: 'Telefoongesprek (ACS)', icon: Phone },
};

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.round(totalSeconds % 60);
  if (hours > 0) return `${hours}u ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function formatCost(cost: number): string {
  return `€${cost.toFixed(4)}`;
}

const MONTH_NAMES = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

function formatMonth(m: string) {
  const [y, mo] = m.split('-');
  return `${MONTH_NAMES[parseInt(mo, 10) - 1]} ${y}`;
}

/**
 * Waar de cijfers vandaan komen, per context.
 *
 * Totalen en maanden komen als aggregaat van de server, over al het verbruik. Dit
 * scherm haalde de laatste 500 regels op en telde die op: wie meer gebruikte, zag
 * te lage totalen en miste de oudere maanden. De regels van een maand komen pas
 * bij het openklappen, met de cursor, zodat ook een drukke maand compleet is.
 */
const ROUTES = {
  eigen: { groepen: '/me/usage/breakdown', regels: '/me/usage', pagina: 500 },
  beheer: { groepen: '/admin/usage/breakdown', regels: '/admin/usage/entries', pagina: 2000 },
} as const;

/** Hoeveel maanden de tabel toont. De totalen erboven gaan over alles. */
const MAANDEN_IN_TABEL = 12;

/**
 * Het kostenoverzicht van één praktijkgebruiker.
 *
 * Dit component staat in twee contexten, en dat maakt uit voor de route:
 *
 *   'eigen'  -- een arts in zijn eigen instellingen. GET /api/me/usage(/breakdown),
 *               en die geven alleen het verbruik van de sessie zelf.
 *   'beheer' -- een beheerder die naar een gebruiker kijkt.
 *               GET /api/admin/usage/breakdown en /entries met een filter.
 *
 * In de bron ging beide gevallen naar get-usage met een practice_user_id in de body
 * en een optioneel token. Die functie controleerde alleen iets áls er een token
 * meekwam: zonder token was het verbruik van elke willekeurige gebruiker op te
 * vragen. Vandaar dat de context hier expliciet is en geen token meer bestaat.
 */
export function UserCostsTab({
  practiceUserId,
  bron = 'beheer',
}: {
  practiceUserId: string;
  bron?: 'eigen' | 'beheer';
}) {
  const [maanden, setMaanden] = useState<Maand[]>([]);
  const [totalen, setTotalen] = useState<Maand | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);
  /** De regels per opengeklapte maand. */
  const [regels, setRegels] = useState<Record<string, UsageLog[] | 'laden' | 'fout'>>({});

  const routes = ROUTES[bron];
  /** Bij 'eigen' bepaalt de sessie wie het is; een filter zou daar niets toevoegen. */
  const filter = bron === 'beheer' ? { practiceUserId } : {};

  useEffect(() => {
    if (!practiceUserId) return;
    setIsLoading(true);
    setRegels({});
    setExpandedMonth(null);

    void (async () => {
      try {
        const antwoord = await api.get<{ groups: Maand[]; totals: Maand }>(routes.groepen, {
          query: { ...(bron === 'beheer' ? { practiceUserId } : {}), groupBy: 'month' },
        });
        setMaanden(antwoord.groups);
        setTotalen(antwoord.totals);
      } catch {
        setMaanden([]);
        setTotalen(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [practiceUserId, bron, routes.groepen]);

  const klapOpen = (maand: string) => {
    if (expandedMonth === maand) {
      setExpandedMonth(null);
      return;
    }
    setExpandedMonth(maand);
    const huidig = regels[maand];
    if (huidig !== undefined && huidig !== 'fout') return;

    setRegels((prev) => ({ ...prev, [maand]: 'laden' }));
    void (async () => {
      try {
        const alle = await haalAlleRegels<UsageLog>(
          routes.regels,
          { ...filter, ...jaarEnMaand(maand) },
          routes.pagina,
        );
        setRegels((prev) => ({ ...prev, [maand]: alle }));
      } catch {
        setRegels((prev) => ({ ...prev, [maand]: 'fout' }));
      }
    })();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalSeconds = totalen?.seconds ?? 0;
  const totalCost = totalen?.selling ?? 0;
  const totalActions = totalen?.entries ?? 0;

  const sortedMonths = [...maanden]
    .sort((a, b) => (b.month ?? '').localeCompare(a.month ?? ''))
    .slice(0, MAANDEN_IN_TABEL);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Totaal gebruik</span>
          </div>
          <p className="text-2xl font-bold">{formatDuration(totalSeconds)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {totalActions} AI-acties totaal
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Euro className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Totale kosten</span>
          </div>
          <p className="text-2xl font-bold">€{totalCost.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">verkoopprijs</p>
        </div>
      </div>

      {/* Monthly summary with expandable details */}
      {sortedMonths.length > 0 && (
        <div>
          <span className="text-sm font-medium">Kosten per maand</span>
          <div className="mt-2 rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium w-8"></th>
                  <th className="text-left px-3 py-2 font-medium">Maand</th>
                  <th className="text-right px-3 py-2 font-medium">Acties</th>
                  <th className="text-right px-3 py-2 font-medium">Tijd</th>
                  <th className="text-right px-3 py-2 font-medium">Kosten</th>
                </tr>
              </thead>
              <tbody>
                {sortedMonths.map((d) => {
                  const m = d.month!;
                  const isExpanded = expandedMonth === m;
                  const geladen = regels[m];
                  return (
                    <Fragment key={m}>
                      <tr
                        className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                        onClick={() => klapOpen(m)}
                      >
                        <td className="px-3 py-1.5">
                          {isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                        </td>
                        <td className="px-3 py-1.5 font-medium">{formatMonth(m)}</td>
                        <td className="text-right px-3 py-1.5">{d.entries}</td>
                        <td className="text-right px-3 py-1.5">{formatDuration(d.seconds)}</td>
                        <td className="text-right px-3 py-1.5">€{d.selling.toFixed(2)}</td>
                      </tr>
                      {isExpanded && (geladen === 'laden' || geladen === 'fout') && (
                        <tr className="border-b last:border-0 bg-muted/20">
                          <td className="px-3 py-1"></td>
                          <td className="px-3 py-1 text-xs text-muted-foreground" colSpan={4}>
                            {geladen === 'laden' ? (
                              <span className="inline-flex items-center gap-1.5">
                                <Loader2 className="h-3 w-3 animate-spin" /> Laden...
                              </span>
                            ) : (
                              'Kon de regels van deze maand niet laden.'
                            )}
                          </td>
                        </tr>
                      )}
                      {isExpanded && Array.isArray(geladen) && geladen.map((log) => {
                        const config = USAGE_TYPE_LABELS[log.usageType] || { label: log.usageType, icon: Clock };
                        const Icon = config.icon;
                        const date = new Date(log.createdAt);
                        return (
                          <tr key={log.id} className="border-b last:border-0 bg-muted/20">
                            <td className="px-3 py-1"></td>
                            <td className="px-3 py-1" colSpan={2}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-3 w-3 text-muted-foreground shrink-0" />
                                <div>
                                  <span className="text-xs">{config.label}</span>
                                  <p className="text-[10px] text-muted-foreground">
                                    {date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' })}
                                    {' '}
                                    {date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="text-right px-3 py-1 text-xs">
                              {log.usageType === 'speech_recording' || log.usageType === 'acs_call'
                                ? formatDuration(Number(log.seconds))
                                : '1×'}
                            </td>
                            <td className="text-right px-3 py-1 text-xs">
                              {log.sellingCost != null ? formatCost(log.sellingCost) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalActions === 0 && (
        <p className="text-sm text-muted-foreground italic">Nog geen gebruik geregistreerd.</p>
      )}
    </div>
  );
}
